import { useAtomValue, useSetAtom } from 'jotai';
import { atom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation,useNavigate, useParams } from 'react-router-dom';

import type { DiscountTab } from '@config/paths/discountTabs';

import { paths } from '@config/paths';

import { DiscountDetailsTab, NEW_DISCOUNT_ID } from '../atoms/discountDetailsDrawerAtoms';
import { currentPageAtom, itemsPerPageAtom } from '../atoms/discountsAtoms';

// Atom to store current discounts list for navigation
export const currentDiscountsListAtom = atom<string[]>([]);

export function useDiscountNavigation() {
	const navigate = useNavigate();
	const location = useLocation();
	const { servicesProviderId, tabId, discountId } = useParams<{
		servicesProviderId: string;
		tabId: string;
		discountId?: string;
	}>();

	// Get current discounts list and pagination state
	const currentDiscountsList = useAtomValue(currentDiscountsListAtom);
	const currentPage = useAtomValue(currentPageAtom);
	const itemsPerPage = useAtomValue(itemsPerPageAtom);
	const setCurrentPage = useSetAtom(currentPageAtom);

	// Track pending navigation after page change
	const pendingNavigationRef = useRef<{
		action: 'next' | 'prev';
		targetTab: DiscountDetailsTab;
	} | null>(null);

	/**
	 * Navigate to discount details with a specific tab
	 */
	const openDiscountDetails = useCallback(
		(discountId: string, detailsTab: DiscountDetailsTab = DiscountDetailsTab.OVERVIEW) => {
			if (!servicesProviderId || !tabId) {
				console.warn(
					'Cannot navigate to discount details: missing servicesProviderId or tabId',
				);
				return;
			}

			const path = paths.discountDetails.getHref(
				servicesProviderId,
				tabId as DiscountTab,
				discountId,
				detailsTab,
			);
			navigate(path + location.search);
		},
		[navigate, servicesProviderId, tabId, location.search],
	);

	/**
	 * Navigate to the create discount drawer using the 'new' sentinel as discountId
	 */
	const openCreateDiscount = useCallback(() => {
		if (!servicesProviderId || !tabId) {
			console.warn('Cannot open create discount: missing servicesProviderId or tabId');
			return;
		}

		const path = paths.discountDetails.getHref(
			servicesProviderId,
			tabId as DiscountTab,
			NEW_DISCOUNT_ID,
			DiscountDetailsTab.OVERVIEW,
		);
		navigate(path + location.search);
	}, [navigate, servicesProviderId, tabId, location.search]);

	/**
	 * Close the discount details drawer and return to the discounts list
	 */
	const closeDiscountDetails = () => {
		if (!servicesProviderId || !tabId) {
			console.warn('Cannot close discount details: missing servicesProviderId or tabId');
			return;
		}

		const path = paths.discountsTab.getHref(servicesProviderId, tabId as DiscountTab);
		navigate(path + location.search);
	};

	/**
	 * Navigate to the previous discount in the list
	 */
	const navigateToPreviousDiscount = (
		currentDiscountId: string,
		detailsTab: DiscountDetailsTab = DiscountDetailsTab.OVERVIEW,
	) => {
		const currentIndex = currentDiscountsList.indexOf(currentDiscountId);

		if (currentIndex > 0) {
			const prevDiscountId = currentDiscountsList[currentIndex - 1];
			openDiscountDetails(prevDiscountId, detailsTab);
		} else if (currentIndex === 0 && currentPage > 1) {
			pendingNavigationRef.current = { action: 'prev', targetTab: detailsTab };
			setCurrentPage(currentPage - 1);
		}
	};

	/**
	 * Navigate to the next discount in the list
	 */
	const navigateToNextDiscount = (
		currentDiscountId: string,
		detailsTab: DiscountDetailsTab = DiscountDetailsTab.OVERVIEW,
	) => {
		const currentIndex = currentDiscountsList.indexOf(currentDiscountId);

		if (currentIndex >= 0 && currentIndex < currentDiscountsList.length - 1) {
			const nextDiscountId = currentDiscountsList[currentIndex + 1];
			openDiscountDetails(nextDiscountId, detailsTab);
		} else if (currentIndex === currentDiscountsList.length - 1) {
			pendingNavigationRef.current = { action: 'next', targetTab: detailsTab };
			setCurrentPage(currentPage + 1);
		}
	};

	/**
	 * Get navigation state for the current discount
	 */
	const getNavigationState = (currentDiscountId: string, totalCount: number) => {
		const currentIndex = currentDiscountsList.indexOf(currentDiscountId);
		const isFirstOnPage = currentIndex === 0;
		const isLastOnPage = currentIndex === currentDiscountsList.length - 1;
		const isFirstPage = currentPage === 1;
		const totalPages = Math.ceil(totalCount / itemsPerPage);
		const isLastPage = currentPage >= totalPages;

		return {
			canGoPrevious: !(isFirstOnPage && isFirstPage),
			canGoNext: !(isLastOnPage && isLastPage),
			currentIndex,
			totalInCurrentPage: currentDiscountsList.length,
		};
	};

	// Effect to handle navigation after page change
	useEffect(() => {
		if (pendingNavigationRef.current && currentDiscountsList.length > 0) {
			const { action, targetTab } = pendingNavigationRef.current;

			if (action === 'next') {
				openDiscountDetails(currentDiscountsList[0], targetTab);
			} else if (action === 'prev') {
				openDiscountDetails(currentDiscountsList[currentDiscountsList.length - 1], targetTab);
			}

			pendingNavigationRef.current = null;
		}
	}, [currentDiscountsList, openDiscountDetails]);

	return {
		openDiscountDetails,
		openCreateDiscount,
		closeDiscountDetails,
		navigateToPreviousDiscount,
		navigateToNextDiscount,
		getNavigationState,
		servicesProviderId,
		tabId,
		discountId,
	};
}
